import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { fetchStoryDetails, updateStory } from '../services/api/story';
import { MediaUpload, type MediaData } from '../components/MediaUpload';
import type { Story } from '../types/database';

const editStorySchema = z.object({
  title: z.string().min(3).max(100),
  content: z.string().min(10),
  media: z.object({
    type: z.enum(['image', 'video', 'audio', 'external']),
    url: z.string().url(),
    method: z.enum(['upload', 'url']),
  }).optional(),
});

type EditStoryFormData = z.infer<typeof editStorySchema>;

function EditStory() {
  const { t } = useTranslation();
  const { bandSlug, storySlug } = useParams<{ bandSlug: string; storySlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [story, setStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [media, setMedia] = useState<MediaData | null>(null);

  const { register, handleSubmit, formState: { errors }, reset } = useForm<EditStoryFormData>({
    resolver: zodResolver(editStorySchema),
  });

  useEffect(() => {
    async function loadStory() {
      if (!bandSlug || !storySlug) return;

      try {
        const storyData = await fetchStoryDetails(bandSlug, storySlug);
        setStory(storyData);
        reset({
          title: storyData.title,
          content: storyData.content,
        });
        
        // Set media if story has an image
        if (storyData.image_url) {
          setMedia({
            type: 'image', // Default to image for existing stories
            url: storyData.image_url,
            method: 'url', // Assume existing images are URLs
          });
        }
      } catch (err) {
        console.error('Error loading story:', err);
        setError(err instanceof Error ? err.message : 'Failed to load story');
      } finally {
        setLoading(false);
      }
    }

    loadStory();
  }, [bandSlug, storySlug, reset]);

  const onSubmit = async (data: EditStoryFormData) => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    if (!story) return;

    try {
      setIsSubmitting(true);
      await updateStory(story.id, {
        title: data.title,
        content: data.content,
        image_url: media?.url || null,
      });
      navigate(`/bands/${bandSlug}`);
    } catch (err) {
      console.error('Error updating story:', err);
      setError(err instanceof Error ? err.message : 'Failed to update story');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="text-center text-light/60">{t('common.loading')}</div>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="text-center text-red-500">{error || 'Story not found'}</div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: t('nav.bands'), href: '/bands' },
    { label: story.band.name, href: `/bands/${bandSlug}` },
    { label: t('stories.editStory.title') },
  ];

  return (
    <div className="container mx-auto px-6 pb-8 md:py-24">
      <div className="mb-6">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      <Title subtitle={t('stories.editStory.subtitle')}>
        {t('stories.editStory.title')}
      </Title>

      <div className="max-w-4xl mx-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="panel">
            <div className="space-y-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-light/80">
                  {t('forms.title')}
                </label>
                <input
                  type="text"
                  id="title"
                  {...register('title')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="content" className="block text-sm font-medium text-light/80">
                  {t('forms.content')}
                </label>
                <textarea
                  id="content"
                  rows={8}
                  {...register('content')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                />
                {errors.content && (
                  <p className="mt-1 text-sm text-red-500">{errors.content.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-light/80 mb-4">
                  Media ({t('common.optional')})
                </label>
                <MediaUpload
                  value={media}
                  onChange={setMedia}
                  storageContainer="story-media"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-end md:space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/bands/${bandSlug}`)}
              className="btn-outline order-2 mt-6 md:mt-0"
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className="btn-secondary md:order-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.saving') : t('forms.saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditStory;